import { API_BASE } from "../consts";

function tryParseJSON(str) {
  if (typeof str !== 'string') return null;
  try {
    return JSON.parse(str);
  } catch (e) {
    return null;
  }
}

// Walks the error body (which may have JSON-encoded strings nested inside
// JSON, e.g. detail / generate_detail / download_detail fields, or arrays
// of shipment objects each carrying their own errors[]) looking for the
// first { errors: [{ description, info }] } shape, at any depth.
function findFirstDescription(value, depth = 0) {
  if (depth > 6 || value === null || value === undefined) return null;

  if (typeof value === 'string') {
    const parsed = tryParseJSON(value);
    return parsed ? findFirstDescription(parsed, depth + 1) : null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findFirstDescription(item, depth + 1);
      if (found) return found;
    }
    return null;
  }

  if (typeof value === 'object') {
    if (Array.isArray(value.errors) && value.errors.length > 0) {
      const err = value.errors[0];
      const desc = err && (err.description || err.info);
      if (desc) return desc;
    }
    for (const key of Object.keys(value)) {
      const found = findFirstDescription(value[key], depth + 1);
      if (found) return found;
    }
  }

  return null;
}

// Only used when nothing structured was found. Pulls a few simple
// (non-object) top-level fields instead of ever dumping the raw JSON.
function buildSummary(data) {
  if (!data || typeof data !== 'object') return String(data);
  const parts = [];
  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined || typeof value === 'object') continue;
    parts.push(`${key}: ${value}`);
    if (parts.length >= 4) break;
  }
  return parts.length > 0 ? parts.join(', ') : 'An unexpected error occurred';
}

function extractServerErrorMessage(data) {
  if (!data || typeof data !== 'object') return null;

  const nested = findFirstDescription(data);
  if (nested) return nested;

  // Plain short strings like "429 Client Error: Too Many Requests for url: ..."
  if (typeof data.detail === 'string' && data.detail.length < 300) return data.detail;

  if (typeof data.error === 'string') return data.error;
  if (typeof data.message === 'string') return data.message;

  return buildSummary(data);
}

const CONTACT_SUFFIX = ' If you need help, contact us and we will help you ASAP.';

function redactSecrets(message) {
  if (!message) return message;
  return String(message)
    .replace(/([a-zA-Z0-9_-]*(?:key|token|secret|password|auth)[a-zA-Z0-9_-]*\s*[:=]\s*)["']?[a-zA-Z0-9_\-./+]{8,}["']?/gi, '')
    .replace(/Bearer\s+[a-zA-Z0-9_\-./+]{8,}/gi, '')
    .replace(/\b(sk|pk|rk)-[a-zA-Z0-9]{16,}\b/gi, '')
    .replace(/\b[a-zA-Z0-9_\-]{24,}\b/g, '');
}

class AuthService {
  constructor() {
    this.API_BASE = `${API_BASE}/api`;
    this.credentials = null;
    this.credentialsPromise = null;
    this._loadCredentials();
  }

  _loadCredentials() {
    if (!this.credentialsPromise) {
      this.credentialsPromise = this.loadCredentialsFromStorage().then(creds => {
        this.credentials = creds;
        return creds;
      });
    }
    return this.credentialsPromise;
  }

  async loadCredentialsFromStorage() {
    try {
      const credentials = localStorage.getItem('credentials');
      return credentials ? JSON.parse(credentials) : null;
    } catch {
      return null;
    }
  }

  async saveCredentialsToStorage(email, password) {
    localStorage.setItem('credentials', JSON.stringify({ email, password }));
  }

  async clearCredentialsFromStorage() {
    localStorage.removeItem('credentials');
    localStorage.removeItem('user'); // Clean up any old cached user data
  }

  async register(email, password, affiliate = '', amazonMarketplace = 'US') {
    try {
      const response = await fetch(`${this.API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, affiliate, amazon_marketplace: amazonMarketplace })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Registration failed');

      this.credentials = { email, password };
      await this.saveCredentialsToStorage(email, password);

      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async login(email, password) {
    try {
      const response = await fetch(`${this.API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Login failed');

      this.credentials = { email, password };
      await this.saveCredentialsToStorage(email, password);

      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async verifyAuth() {
    if (!this.credentials && this.credentialsPromise) {
      await this.credentialsPromise;
    }

    if (!this.credentials) {
      return { valid: false };
    }

    try {
      const response = await fetch(`${this.API_BASE}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.credentials)
      });

      const data = await response.json();

      if (data.valid && data.user?.amazon_marketplace) {
        const domainMap = { 'US': '.com', 'UK': '.co.uk', 'CA': '.ca', 'MX': '.com.mx' };
        this._amazonMarketplace = domainMap[data.user.amazon_marketplace] || '.com';
      }
      return {
        valid: data.valid,
        user: data.user
      };
    } catch {
      return { valid: false };
    }
  }

  async getUser() {
    const authResult = await this.verifyAuth();
    return authResult.valid ? authResult.user : null;
  }

  async getUserAsync() {
    return this.getUser();
  }

  async logout() {
    this.credentials = null;
    this.credentialsPromise = null;
    await this.clearCredentialsFromStorage();
  }

  getAmazonMarketplace() {
    return this._amazonMarketplace || '.com';
  }

  getAmazonCurrency() {
    return this._amazonMarketplace === '.co.uk' ? '£' : '$';
  }

  isAuthenticated() {
    return !!this.credentials;
  }

  async addToBuyList(listName, marketplace, itemData) {
    try {
      const response = await this.authenticatedFetch(`${this.API_BASE}/buy-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ list_name: listName, marketplace: marketplace, data: itemData })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async exportBuyList(listName, marketplace) {
    try {
      const response = await this.authenticatedFetch(`${this.API_BASE}/buy-list/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ list_name: listName, marketplace: marketplace })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      return { success: true, csvData: data.csv_data, count: data.count, item_ids: data.item_ids };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getBuyListNames(marketplace) {
    try {
      const response = await this.authenticatedFetch(`${this.API_BASE}/buy-list/names`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      if (marketplace && marketplace !== 'all') {
        return { success: true, listNames: data.grouped[marketplace] || [] };
      }

      const allLists = [];
      Object.entries(data.grouped || {}).forEach(([platform, names]) => {
        names.forEach(name => allLists.push({ name, platform }));
      });
      return { success: true, listNames: allLists, grouped: data.grouped };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async deleteBuyListItem(itemId) {
    try {
      const response = await this.authenticatedFetch(`${this.API_BASE}/buy-list/delete-item`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async updateBuyListItem(itemId, newData) {
    try {
      const response = await this.authenticatedFetch(`${this.API_BASE}/buy-list/update-item`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId, data: newData })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async deleteBuyList(listName, marketplace) {
    try {
      const response = await this.authenticatedFetch(`${this.API_BASE}/buy-list/delete-list`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ list_name: listName, marketplace: marketplace })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async checkItemExists(identifier, marketplace) {
    try {
      const response = await this.authenticatedFetch(
        `${this.API_BASE}/buy-list/check-item?identifier=${encodeURIComponent(identifier)}&marketplace=${marketplace}`
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      return { success: true, exists: data.exists, listNames: data.list_names || [] };
    } catch (error) {
      return { success: false, exists: false, listNames: [] };
    }
  }

  async removeFromBuyListByIdentifier(identifier, listName, marketplace) {
    try {
      const response = await this.authenticatedFetch(`${this.API_BASE}/buy-list/remove-by-identifier`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, list_name: listName, marketplace: marketplace })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  getAuthHeaders() {
    if (!this.credentials) {
      return {};
    }

    return {
      'X-User-Email': this.credentials.email,
      'X-User-Password': this.credentials.password
    };
  }

  async authenticatedFetch(url, options = {}, retries = 2) {
    const authHeaders = this.getAuthHeaders();

    let lastError;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            ...authHeaders
          }
        });

        if (response.status >= 500) {
          let serverMessage = `Error Message: ${response.status}`;
          try {
            const data = await response.clone().json();
            serverMessage = extractServerErrorMessage(data) || serverMessage;
          } catch (e) {
            // body wasn't JSON or was empty, fall back to default
          }

          if (/429|Too Many Requests/i.test(serverMessage)) {
            throw new Error("You hit rate limit (sent too many requests in a short time period), please wait 10 minutes before trying again. Also, please watch this video to see the best practice for how to use MSP Inventory: https://www.youtube.com/watch?v=L-ffyZAuQNM");
          }

          serverMessage = redactSecrets(serverMessage);
          if (!/contact us/i.test(serverMessage)) {
            serverMessage += CONTACT_SUFFIX;
          }

          lastError = new Error(serverMessage);
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            continue;
          }
          throw lastError;
        }

        if (response.status === 503) {
          throw new Error('Service temporarily unavailable');
        }

        if (response.status === 401) {
          if (attempt < retries) {
            await new Promise(resolve => setTimeout(resolve, 500));
            continue;
          }
          const data = await response.json();
          if (data.error === 'Invalid password' || data.error === 'User not found') {
            await this.logout();
            window.location.reload();
          }
          throw new Error('Authentication required');
        }

        if (response.status === 402) {
          const data = await response.json();
          throw new SubscriptionRequiredError(data.error, data.code, data.required_plan);
        }

        return response;

      } catch (error) {
        if (error instanceof SubscriptionRequiredError) throw error;
        if (error.message === 'Authentication required') throw error;
        if (error.message === 'Service temporarily unavailable') throw error;
        lastError = error;
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
          continue;
        }
      }
    }

    throw lastError;
  }
}

// Custom error for subscription required
class SubscriptionRequiredError extends Error {
  constructor(message, code, requiredPlan) {
    super(message);
    this.name = 'SubscriptionRequiredError';
    this.code = code;
    this.requiredPlan = requiredPlan;
  }
}

// Create singleton instance
const authService = new AuthService();
export default authService;